import { tokensToCss, readFontImports, type TokenNode } from './themeToCss';

/**
 * Emit the tenant's design tokens as CSS custom properties on
 * {@code :root}. Server-safe — no client APIs touched.
 *
 * <p>Port of ThemeMaker's {@code components/shell/ThemeStyle.tsx}.
 *
 * <h3>Why inline {@code <style>} instead of a stylesheet link</h3>
 * <ul>
 *   <li>Tokens are per-tenant and arrive in the render payload at request
 *       time. Pre-building a per-tenant stylesheet would complicate
 *       routing for no real win.</li>
 *   <li>Inlining keeps the FOUC-free guarantee — tokens land in the
 *       same HTML document as the markup that uses them.</li>
 *   <li>Size is tiny (a few KB), gzips well.</li>
 * </ul>
 *
 * <p>{@code dangerouslySetInnerHTML} is correct here: React needs to emit
 * the CSS as raw text inside a {@code <style>} tag, not as React DOM.
 *
 * <h3>Google-Fonts-style {@code font.$imports[].url} entries</h3>
 * Rendered as real {@code <link rel="stylesheet">} tags (plus a
 * preconnect hint), not folded into the inline {@code <style>} block as
 * a CSS {@code @import}. A {@code @import} inside an inline style tag is
 * invisible to the browser's preload scanner — it's only discovered once
 * the parser reaches that point in the document AND starts parsing the
 * style block's contents — and it blocks the rest of that same style
 * block (i.e. every token CSS variable) from applying until the import
 * resolves. A {@code <link>} tag is discovered immediately and fetched
 * in parallel with everything else.
 *
 * <h3>Non-render-blocking load</h3>
 * Each font link ships as {@code media="print"} (fetched at full
 * priority, but doesn't block the initial render since no screen matches
 * "print") plus a tiny inline script that flips it to {@code media="all"}
 * once loaded — the standard "loadCSS" pattern. A {@code <noscript>}
 * fallback keeps it synchronous for JS-disabled visitors. This trades a
 * brief flash-of-fallback-font (the tenant's custom font swaps in a beat
 * after first paint instead of blocking first paint on it) for a faster
 * LCP — the swap script can't be a React {@code onLoad} prop because this
 * component is a Server Component (no client APIs) and, separately,
 * {@code onLoad} needs a function, not the string a plain HTML attribute
 * would take.
 *
 * <p>The swap script runs during HTML parsing, i.e. before React hydrates,
 * so by hydration time the live DOM already reads {@code media="all"}
 * while React's render output still says {@code media="print"}. That is a
 * deliberate out-of-band mutation, hence {@code suppressHydrationWarning}
 * on the link — without it React logs a hydration-mismatch error on every
 * page load. React leaves the attribute alone either way, so the swapped
 * {@code media="all"} survives.
 *
 * <p>Renders {@code null} when {@code tokens} is null/undefined so
 * callers can pass the renderer payload directly without conditionals.
 */
export function ThemeTokens({
  tokens,
  nonce,
}: {
  tokens: TokenNode | null | undefined;
  /** Optional CSP nonce — forwarded to the style tag so strict CSP setups can allow this inline style. */
  nonce?: string;
}) {
  if (!tokens) return null;
  const css = tokensToCss(tokens, { skipImports: true });
  const fontImports = readFontImports(tokens).filter((fi) => fi.url);
  if (!css && fontImports.length === 0) return null;

  // One preconnect per distinct font-CSS origin, plus fonts.gstatic.com
  // when the origin is Google Fonts — its CSS response references that
  // second origin for the actual font files, the classic Google Fonts
  // two-hop load.
  const preconnectOrigins = new Set<string>();
  for (const fi of fontImports) {
    try {
      const origin = new URL(fi.url!).origin;
      preconnectOrigins.add(origin);
      if (origin === 'https://fonts.googleapis.com') {
        preconnectOrigins.add('https://fonts.gstatic.com');
      }
    } catch {
      // Malformed URL — skip the preconnect hint, the <link rel="stylesheet"> below still tries it.
    }
  }

  return (
    <>
      {[...preconnectOrigins].map((origin) => (
        <link key={origin} rel="preconnect" href={origin} crossOrigin="anonymous" />
      ))}
      {fontImports.map((fi) => (
        <link
          key={fi.url}
          rel="stylesheet"
          href={fi.url}
          media="print"
          data-font-swap=""
          suppressHydrationWarning
        />
      ))}
      {fontImports.length > 0 && (
        <script
          nonce={nonce}
          dangerouslySetInnerHTML={{
            __html:
              "document.querySelectorAll('link[data-font-swap]').forEach(function(l){l.sheet?l.media='all':l.addEventListener('load',function(){l.media='all'})});",
          }}
        />
      )}
      <noscript>
        {fontImports.map((fi) => (
          <link key={fi.url} rel="stylesheet" href={fi.url} />
        ))}
      </noscript>
      {css && <style nonce={nonce} dangerouslySetInnerHTML={{ __html: css }} />}
    </>
  );
}
